import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import RoleSelect from "./pages/RoleSelect";
import Dashboard from "./pages/Dashboard";
import BrowseWorkers from "./pages/BrowseWorkers";
import WorkerProfile from "./pages/WorkerProfile";
import JobsList from "./pages/JobsList";
import PostJob from "./pages/PostJob";
import JobDetail from "./pages/JobDetail";
import { ChatList, ChatRoom } from "./pages/Chat";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import Report from "./pages/Report";
import Legal from "./pages/Legal";
import Support from "./pages/Support";
import WorkerOnboarding from "./pages/WorkerOnboarding";
import WorkerVerification from "./pages/WorkerVerification";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";
import { AdminBlog, AdminBlogEditor } from "./pages/AdminBlog";
import LocationLanding from "./pages/LocationLanding";
import VirtualLanding from "./pages/virtual/VirtualLanding";
import VirtualJobsList from "./pages/virtual/VirtualJobsList";
import VirtualPostJob from "./pages/virtual/VirtualPostJob";
import VirtualJobDetail from "./pages/virtual/VirtualJobDetail";
import VirtualFreelancersList from "./pages/virtual/VirtualFreelancersList";
import VirtualFreelancerProfile from "./pages/virtual/VirtualFreelancerProfile";
import VirtualProfileEditor from "./pages/virtual/VirtualProfileEditor";
import VirtualServiceLanding from "./pages/virtual/VirtualServiceLanding";
import VirtualPayouts from "./pages/virtual/VirtualPayouts";
import VirtualClientDashboard from "./pages/virtual/VirtualClientDashboard";
import VirtualFreelancerDashboard from "./pages/virtual/VirtualFreelancerDashboard";
import ProtectedRoute from "./components/ProtectedRoute";

function AppRouter() {
  const location = useLocation();
  // Detect session_id during render (not useEffect) to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route path="/role-select" element={<ProtectedRoute><RoleSelect /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

      <Route path="/workers" element={<BrowseWorkers />} />
      <Route path="/workers/:id" element={<WorkerProfile />} />

      <Route path="/jobs" element={<JobsList />} />
      <Route path="/jobs/new" element={<ProtectedRoute requireRole="customer"><PostJob /></ProtectedRoute>} />
      <Route path="/jobs/:id" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />

      <Route path="/chat" element={<ProtectedRoute><ChatList /></ProtectedRoute>} />
      <Route path="/chat/:id" element={<ProtectedRoute><ChatRoom /></ProtectedRoute>} />

      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />

      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/report" element={<ProtectedRoute><Report /></ProtectedRoute>} />
      <Route path="/support" element={<Support />} />

      <Route path="/worker/onboarding" element={<ProtectedRoute requireRole="worker"><WorkerOnboarding /></ProtectedRoute>} />
      <Route path="/worker/verification" element={<ProtectedRoute requireRole="worker"><WorkerVerification /></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
      <Route path="/admin/blog/new" element={<ProtectedRoute><AdminBlogEditor /></ProtectedRoute>} />
      <Route path="/admin/blog/edit/:slug" element={<ProtectedRoute><AdminBlogEditor /></ProtectedRoute>} />

      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      <Route path="/services/:category/:city" element={<LocationLanding />} />

      {/* Virtual Staff section */}
      <Route path="/virtual" element={<VirtualLanding />} />
      <Route path="/virtual/jobs" element={<VirtualJobsList />} />
      <Route path="/virtual/jobs/new" element={<ProtectedRoute><VirtualPostJob /></ProtectedRoute>} />
      <Route path="/virtual/jobs/:id" element={<VirtualJobDetail />} />
      <Route path="/virtual/freelancers" element={<VirtualFreelancersList />} />
      <Route path="/virtual/freelancers/:id" element={<VirtualFreelancerProfile />} />
      <Route path="/virtual/freelancer/profile" element={<ProtectedRoute><VirtualProfileEditor /></ProtectedRoute>} />
      <Route path="/virtual/freelancer/payouts" element={<ProtectedRoute><VirtualPayouts /></ProtectedRoute>} />
      <Route path="/virtual/dashboard/client" element={<ProtectedRoute><VirtualClientDashboard /></ProtectedRoute>} />
      <Route path="/virtual/dashboard/freelancer" element={<ProtectedRoute><VirtualFreelancerDashboard /></ProtectedRoute>} />
      <Route path="/virtual/services/:slug" element={<VirtualServiceLanding />} />

      <Route path="/legal/privacy" element={<Legal slug="privacy" />} />
      <Route path="/legal/terms" element={<Legal slug="terms" />} />
      <Route path="/legal/refund" element={<Legal slug="refund" />} />
      <Route path="/legal/community" element={<Legal slug="community" />} />
      <Route path="/legal/contractor" element={<Legal slug="contractor" />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
